import React, { FC } from 'react';
/* eslint-disable import/no-duplicates */
import { format, startOfYear, addSeconds } from 'date-fns';
import ja from 'date-fns/locale/ja';
/* eslint-disable import/no-duplicates */

import { EventSchemeResource } from '../../../../../../../../../interfaces/reservation_flow';
import { RSDescriptionList } from '../../../../../../../../../reservation-page/description-list/RSDescriptionList';
import { RSDescriptionListItem } from '../../../../../../../../../reservation-page/description-list/RSDescriptionListItem';

export interface CautionListProps {
  resource: EventSchemeResource;
  isPrepaymentEnabled: boolean;
}

export const CautionList: FC<CautionListProps> = ({
  resource,
  isPrepaymentEnabled,
}) => {
  return (
    <RSDescriptionList>
      {(() => {
        const { start_period } = resource.reservable_period;
        if (start_period === null) {
          return null;
        }

        if (start_period.type === 'no_setup') {
          return null;
        }

        switch (start_period.type) {
          case 'datetime': {
            if (start_period.datetime === null) {
              return null;
            }

            return (
              <RSDescriptionListItem
                term="予約の受付開始"
                description={
                  <div className="rs-font text-16 font-normal text-gray-1000 pc:text-15">
                    {`${format(
                      new Date(start_period.datetime),
                      'YYYY年M月D日H時m分',
                      { locale: ja },
                    )}から`}
                  </div>
                }
              />
            );
          }
          case 'relative_month': {
            if (start_period.relative_month === null) {
              return null;
            }

            const { base_months, seconds_from_base_months } =
              start_period.relative_month;

            // 日付計算を行うため年の最初からの経過秒数を取得
            const dateForCalc = addSeconds(
              startOfYear(new Date()),
              seconds_from_base_months,
            );

            const baseMonthString = (() => {
              if (base_months === 0) {
                return '当月';
              }

              if (base_months === 1) {
                return '前月';
              }

              return `${base_months}ヶ月前`;
            })();

            return (
              <RSDescriptionListItem
                term="予約の受付開始"
                description={
                  <div className="rs-font text-16 font-normal text-gray-1000 pc:text-15">
                    {`${baseMonthString}の${format(
                      dateForCalc,
                      'D日H時m分',
                    )}から`}
                  </div>
                }
              />
            );
          }
          case 'relative_day': {
            if (start_period.relative_day === null) {
              return null;
            }

            const { base_days, seconds_from_base_days } =
              start_period.relative_day;

            // 日付計算を行うため年の最初からの経過秒数を取得
            const dateForCalc = addSeconds(
              startOfYear(new Date()),
              seconds_from_base_days,
            );

            const baseDayString = (() => {
              if (base_days === 0) {
                return '当日';
              }

              return `${base_days}日前`;
            })();

            return (
              <RSDescriptionListItem
                term="予約の受付開始"
                description={
                  <div className="rs-font text-16 font-normal text-gray-1000 pc:text-15">
                    {`${baseDayString}の${format(dateForCalc, 'H時m分')}から`}
                  </div>
                }
              />
            );
          }
        }
      })()}

      {(() => {
        const { end_period } = resource.reservable_period;
        if (end_period === null) {
          return null;
        }

        if (end_period.type === 'no_setup') {
          return null;
        }

        switch (end_period.type) {
          case 'datetime': {
            if (end_period.datetime === null) {
              return null;
            }

            return (
              <RSDescriptionListItem
                term="予約の受付締切"
                description={
                  <div className="rs-font text-16 font-normal text-gray-1000 pc:text-15">
                    {`${format(
                      new Date(end_period.datetime),
                      'YYYY年M月D日H時m分',
                      {
                        locale: ja,
                      },
                    )}まで`}
                  </div>
                }
              />
            );
          }
          case 'relative_month': {
            if (end_period.relative_month === null) {
              return null;
            }

            const { base_months, seconds_from_base_months } =
              end_period.relative_month;

            // 日付計算を行うため年の最初からの経過秒数を取得
            const dateForCalc = addSeconds(
              startOfYear(new Date()),
              seconds_from_base_months,
            );

            const baseMonthString = (() => {
              if (base_months === 0) {
                return '当月';
              }

              if (base_months === 1) {
                return '前月';
              }

              return `${base_months}ヶ月前`;
            })();

            return (
              <RSDescriptionListItem
                term="予約の受付締切"
                description={
                  <div className="rs-font text-16 font-normal text-gray-1000 pc:text-15">
                    {`${baseMonthString}の${format(
                      dateForCalc,
                      'D日H時m分',
                    )}まで`}
                  </div>
                }
              />
            );
          }
          case 'relative_day': {
            if (end_period.relative_day === null) {
              return null;
            }

            const { base_days, seconds_from_base_days } =
              end_period.relative_day;

            // 日付計算を行うため年の最初からの経過秒数を取得
            const dateForCalc = addSeconds(
              startOfYear(new Date()),
              seconds_from_base_days,
            );

            const baseDayString = (() => {
              if (base_days === 0) {
                return '当日';
              }

              return `${base_days}日前`;
            })();

            return (
              <RSDescriptionListItem
                term="予約の受付締切"
                description={
                  <div className="rs-font text-16 font-normal text-gray-1000 pc:text-15">
                    {`${baseDayString}の${format(dateForCalc, 'H時m分')}まで`}
                  </div>
                }
              />
            );
          }
          case 'relative': {
            if (end_period.relative === null) {
              return null;
            }

            const { seconds_from_base_checkin } = end_period.relative;

            // 単位が日の場合
            if (seconds_from_base_checkin > 3600) {
              return (
                <RSDescriptionListItem
                  term="予約の受付締切"
                  description={
                    <div className="rs-font text-16 font-normal text-gray-1000 pc:text-15">
                      {`${seconds_from_base_checkin / 3600}時間前まで`}
                    </div>
                  }
                />
              );
            }

            return (
              <RSDescriptionListItem
                term="予約の受付締切"
                description={
                  <div className="rs-font text-16 font-normal text-gray-1000 pc:text-15">
                    {`${seconds_from_base_checkin / 60}分前まで`}
                  </div>
                }
              />
            );
          }
        }
      })()}

      <RSDescriptionListItem
        term="日程変更"
        description={(() => {
          if (
            resource.changeable_until === null ||
            resource.changeable_until === 0
          ) {
            return (
              <div className="rs-font text-16 font-normal text-gray-1000 pc:text-15">
                日程変更を受け付けておりません。
              </div>
            );
          }

          // 単位が日の場合
          if (resource.changeable_until >= 86400) {
            return (
              <div className="rs-font text-16 font-normal text-gray-1000 pc:text-15">
                {`予約日時の${resource.changeable_until / 86400}日前まで`}
              </div>
            );
          }

          return (
            <div className="rs-font text-16 font-normal text-gray-1000 pc:text-15">
              {`予約日時の${resource.changeable_until / 3600}時間前まで`}
            </div>
          );
        })()}
      />

      <RSDescriptionListItem
        term="キャンセル"
        description={(() => {
          if (
            resource.cancelable_until === null ||
            resource.cancelable_until === 0
          ) {
            return (
              <div className="rs-font text-16 font-normal text-gray-1000 pc:text-15">
                キャンセルを受け付けておりません。
              </div>
            );
          }

          // 単位が日の場合
          if (resource.cancelable_until >= 86400) {
            return (
              <div>
                <div className="rs-font text-16 font-normal text-gray-1000 pc:text-15">
                  {`予約日時の${resource.cancelable_until / 86400}日前まで`}
                </div>
                {isPrepaymentEnabled && (
                  <div className="rs-font text-13 font-normal text-gray-1000 pc:text-12">
                    ※ただし、事前決済の場合、キャンセル期限を過ぎていなくても、予約から
                    25 日が過ぎるとキャンセルできません
                  </div>
                )}
              </div>
            );
          }

          return (
            <div>
              <div className="rs-font text-16 font-normal text-gray-1000 pc:text-15">
                {`予約日時の${resource.cancelable_until / 3600}時間前まで`}
              </div>
              {isPrepaymentEnabled && (
                <div className="rs-font text-13 font-normal text-gray-1000 pc:text-12">
                  ※ただし、事前決済の場合、キャンセル期限を過ぎていなくても、予約から
                  25 日が過ぎるとキャンセルできません
                </div>
              )}
            </div>
          );
        })()}
      />
    </RSDescriptionList>
  );
};
